/**
 * Número que rola (design system de movimento, "Número que muda no botão", 23/10/2026): só o
 * número troca, rolando para cima quando sobe e para baixo quando desce (.ds-rola, 200 ms com a
 * curva de movimento, algarismos de largura igual). No máximo 4 rolagens por segundo; acima disso,
 * o número salta para o valor atual. Sem movimento, movimento.css troca a rolagem por um fade de
 * 120 ms. Sem CSS (testes), o token vale 0 e o número troca na hora.
 *
 * - `Rola`: só o número (contagem das pílulas). O valor que sai fica fora do leitor de tela.
 * - `TextoQueRola`: frase com números ("2 listas selecionadas", "3 telefones de 2 pessoas"). A
 *   parte visível rola cada número no seu lugar e troca o resto do texto na hora. O leitor de
 *   tela ouve a frase inteira: com `classeLeitor`, numa cópia escondida da vista (e a parte
 *   visível fica fora do leitor); sem ela (null), na própria frase, e quem chama põe
 *   aria-atomic na região viva para ela ser lida inteira.
 */
import { Fragment, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { tokenMs } from '../../lib/movimento';
import { numeroDe } from './movimentoListas';

/** 4 rolagens por segundo. */
const TETO_MS = 250;

interface Estado {
  valor: number;
  texto: string;
  /** Cada troca monta um elemento novo, para a animação de entrada rodar de novo. */
  vez: number;
  anima: boolean;
  desce: boolean;
}

export function Rola({ valor, texto }: { valor: number; texto?: string }): JSX.Element {
  const mostrar = texto ?? String(valor);
  const [atual, setAtual] = useState<Estado>({
    valor,
    texto: mostrar,
    vez: 0,
    anima: false,
    desce: false,
  });
  const [saindo, setSaindo] = useState<Omit<Estado, 'valor' | 'anima'> | null>(null);
  const ultimaRolagem = useRef(-Infinity);

  // Antes de pintar: o número velho nunca aparece com o texto novo ao lado.
  useLayoutEffect(() => {
    if (valor === atual.valor) {
      if (mostrar !== atual.texto) setAtual((a) => ({ ...a, texto: mostrar }));
      return;
    }
    const agora = performance.now();
    const anima = tokenMs('--duracao-200') > 0 && agora - ultimaRolagem.current >= TETO_MS;
    if (anima) ultimaRolagem.current = agora;
    const desce = valor < atual.valor;
    const vez = atual.vez + 1;
    setSaindo(anima ? { texto: atual.texto, vez, desce } : null);
    setAtual({ valor, texto: mostrar, vez, anima, desce });
  }, [valor, mostrar, atual]);

  // O que saiu some depois da rolagem (--duracao-200).
  useEffect(() => {
    if (!saindo) return undefined;
    const timer = window.setTimeout(() => setSaindo(null), tokenMs('--duracao-200'));
    return () => window.clearTimeout(timer);
  }, [saindo]);

  const sentido = (desce: boolean): string => (desce ? ' ds-desce' : '');
  return (
    <span className="ds-rola">
      {saindo ? (
        <span key={`s${saindo.vez}`} className={`ds-rola-sai${sentido(saindo.desce)}`} aria-hidden="true">
          {saindo.texto}
        </span>
      ) : null}
      <span
        key={`e${atual.vez}`}
        className={atual.anima ? `ds-rola-entra${sentido(atual.desce)}` : undefined}
      >
        {atual.texto}
      </span>
    </span>
  );
}

/** Números da frase: "1.234", "48", "2". */
const NUMERO = /(\d[\d.,]*\d|\d)/;

export function TextoQueRola({
  texto,
  classeLeitor = 'lt-sr',
}: {
  texto: string;
  /** Classe que esconde a cópia só da vista (lt-sr de listsTable.css); null: sem cópia. */
  classeLeitor?: string | null;
}): JSX.Element {
  // split com o grupo: as posições ímpares são os números.
  const partes = texto.split(NUMERO).map((parte, i) =>
    i % 2 ? (
      <Rola key={i} valor={numeroDe(parte)} texto={parte} />
    ) : (
      <Fragment key={i}>{parte}</Fragment>
    ),
  );
  if (classeLeitor === null) return <>{partes}</>;
  return (
    <>
      <span className={classeLeitor}>{texto}</span>
      <span aria-hidden="true">{partes}</span>
    </>
  );
}
